import React from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Shield, Star, Crown, Zap, ArrowUp } from 'lucide-react';

const ROLE_CONFIG = {
  owner: { label: 'OWNER', icon: Crown, gradient: 'from-amber-500 to-yellow-400', particle: '#FFD700' },
  overseer: { label: 'OVERSEER', icon: Zap, gradient: 'from-teal-400 to-cyan-400', particle: '#00CED1' },
  admin: { label: 'ADMIN', icon: Shield, gradient: 'from-red-500 to-pink-400', particle: '#FF6B6B' },
  artist: { label: 'ARTIST', icon: Star, gradient: 'from-purple-500 to-violet-400', particle: '#9B59B6' },
  vip: { label: 'VIP', icon: Star, gradient: 'from-blue-500 to-indigo-400', particle: '#3498DB' },
  user: { label: 'MEMBER', icon: ArrowUp, gradient: 'from-gray-400 to-gray-300', particle: '#FFFFFF' },
};

const RoleChangeBanner = ({ isVisible, newRole, changedBy, onDismiss }) => {
  const config = ROLE_CONFIG[newRole] || ROLE_CONFIG.user;
  const Icon = config.icon;

  return (
    <AnimatePresence>
      {isVisible && (
        <motion.div
          initial={{ opacity: 0, scale: 0.8, y: -40 }}
          animate={{ opacity: 1, scale: 1, y: 0 }}
          exit={{ opacity: 0, scale: 0.8, y: -40 }}
          transition={{ type: 'spring', stiffness: 200, damping: 20 }}
          className="fixed top-20 left-1/2 -translate-x-1/2 z-[60] pointer-events-auto"
          onClick={onDismiss}
          data-testid="role-change-banner"
        >
          {/* Glow backdrop */}
          <motion.div
            className={`absolute inset-0 rounded-2xl bg-gradient-to-r ${config.gradient} blur-xl opacity-30`}
            animate={{ opacity: [0.2, 0.4, 0.2] }}
            transition={{ duration: 2, repeat: Infinity }}
          />
          
          <div className={`relative flex items-center gap-4 px-6 py-4 rounded-2xl bg-gradient-to-r ${config.gradient} shadow-2xl`}>
            {/* Particles */}
            {[...Array(6)].map((_, i) => (
              <motion.div
                key={i}
                className="absolute w-1.5 h-1.5 rounded-full"
                style={{ background: config.particle }}
                initial={{ 
                  x: 0, y: 0, opacity: 1, scale: 1 
                }}
                animate={{ 
                  x: (Math.random() - 0.5) * 120, 
                  y: (Math.random() - 0.5) * 80,
                  opacity: 0,
                  scale: 0 
                }}
                transition={{ 
                  duration: 1.5 + Math.random(), 
                  repeat: Infinity, 
                  delay: i * 0.3 
                }}
              />
            ))}

            <div className="p-2 rounded-xl bg-white/20 backdrop-blur">
              <Icon className="w-6 h-6 text-white" />
            </div>
            <div>
              <p className="text-white/80 text-xs font-medium tracking-wider uppercase">Role Updated</p>
              <p className="text-white text-lg font-bold tracking-wide">{config.label}</p>
              {changedBy && (
                <p className="text-white/60 text-[11px] mt-0.5">by {changedBy}</p>
              )}
            </div>
          </div>
        </motion.div>
      )}
    </AnimatePresence>
  );
};

export default RoleChangeBanner;
